/** Saved security fields used to resolve backsolve basket members. */
export interface PersistedSecurity {
  id?: number | null;
  name?: string | null;
  security_group_ref?: string | null;
}

/** Minimum security fields the batch-delete planner needs from a visible column. */
export interface ColumnDeleteCandidate extends PersistedSecurity {
  order?: number | null;
  has_multiple_investments?: boolean | null;
  multiple_investments?: unknown[] | null;
  backsolve_usage_details?: {
    is_used_in_backsolve?: boolean;
    /** Security ids of each backsolve basket the column is in, aligned with `location`. */
    basket_member_ids?: number[][];
    location?: string[] | string;
  } | null;
  fod_usage_details?: {
    is_used_in_fod?: boolean;
    location?: string[] | string;
  } | null;
}

/** A backsolve basket a delete would leave with no securities. */
export interface EmptiedBacksolveBasket {
  names: string[];
  location?: string;
}

export interface BlockedColumnDelete {
  order: number;
  baskets: EmptiedBacksolveBasket[];
}

export interface ColumnDeletePlan {
  /** column.order values to mark is_deleted, in first-seen order. */
  deletableOrders: number[];
  /** subset that must cascade into FOD / backsolve / CFL. */
  cascadeOrders: number[];
  /** columns whose deletion would leave a backsolve basket with no securities. */
  blocked: BlockedColumnDelete[];
  names: string[];
  messages: string[];
  unknownOrders: number[];
}

export type ColumnDeletePresentation =
  | { kind: 'noop' }
  | { kind: 'blocked'; reasons: string[] }
  | { kind: 'confirm'; title: string; reasons: string[]; messages: string[]; confirmLabel: string };

const asList = (value: string[] | string | undefined): string[] => {
  if (Array.isArray(value)) {
    return value;
  }
  return value ? [value] : [];
};

const nameOf = (column: ColumnDeleteCandidate, order: number): string => column.name?.trim() || `column ${order}`;

/** Saved security ids a column removes; a grouped (virtual) column removes its whole group. */
export const securityIdsOf = (column: PersistedSecurity, securities: PersistedSecurity[]): number[] => {
  if (column.id) {
    return [column.id];
  }
  const groupRef = column.security_group_ref;
  return groupRef
    ? securities.flatMap(security => (security.id && security.security_group_ref === groupRef ? [security.id] : []))
    : [];
};

/**
 * Backsolve baskets that deleting `candidates`, on top of the already deleted securities,
 * would leave with no securities, keyed by the candidate in that basket.
 */
export const findEmptiedBacksolveBaskets = <T extends ColumnDeleteCandidate>(
  candidates: T[],
  securities: PersistedSecurity[],
  alreadyDeletedIds: number[] = []
): Map<T, EmptiedBacksolveBasket[]> => {
  const deletedIds = new Set([
    ...alreadyDeletedIds,
    ...candidates.flatMap(candidate => securityIdsOf(candidate, securities)),
  ]);
  const nameById = new Map(
    [...securities, ...candidates].flatMap(security => (security.id ? [[security.id, security.name] as const] : []))
  );
  const emptied = new Map<T, EmptiedBacksolveBasket[]>();
  candidates.forEach(candidate => {
    const locations = asList(candidate.backsolve_usage_details?.location);
    const baskets = (candidate.backsolve_usage_details?.basket_member_ids ?? []).flatMap((memberIds, index) =>
      memberIds.length && memberIds.every(id => deletedIds.has(id))
        ? [{ names: memberIds.map(id => nameById.get(id)?.trim() || `security ${id}`), location: locations[index] }]
        : []
    );
    if (baskets.length) {
      emptied.set(candidate, baskets);
    }
  });
  return emptied;
};

const joinNames = (names: string[]): string =>
  names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names.join('');

/** One plain sentence per emptied basket, explaining why the delete is refused. */
export const describeEmptiedBaskets = (baskets: EmptiedBacksolveBasket[]): string[] => [
  ...new Set(
    baskets.map(
      ({ names, location }) =>
        `Deleting ${joinNames(names)} would leave the backsolve basket${location ? ` in ${location}` : ''} empty. ` +
        'Update the backsolve first.'
    )
  ),
];

/**
 * Decide which selected security columns can be deleted together.
 * `columnOrders` are grid column positions from ColumnIndicator. They match `column.order`
 * because the cap table reverse-parse renumbers visible columns 0..N-1 on every sheet update,
 * so columns are looked up by order, never by their position in `visibleColumns`.
 * `securities` are the saved securities and `alreadyDeletedIds` the ones already queued for deletion.
 */
export const planCapTableColumnDeletes = (
  columnOrders: number[],
  visibleColumns: ColumnDeleteCandidate[],
  securities: PersistedSecurity[] = [],
  alreadyDeletedIds: number[] = []
): ColumnDeletePlan => {
  const byOrder = new Map<number, ColumnDeleteCandidate>();
  visibleColumns.forEach(column => {
    if (column.order != null) {
      byOrder.set(column.order, column);
    }
  });

  const deletableOrders: number[] = [];
  const cascadeOrders: number[] = [];
  const blocked: BlockedColumnDelete[] = [];
  const names: string[] = [];
  const messages: string[] = [];
  const unknownOrders: number[] = [];
  const impactSeen = new Set<string>();
  const pushImpact = (line: string) => {
    if (!impactSeen.has(line)) {
      impactSeen.add(line);
      messages.push(line);
    }
  };

  const selected: [number, ColumnDeleteCandidate][] = [];
  new Set(columnOrders).forEach(order => {
    const column = byOrder.get(order);
    if (column) {
      selected.push([order, column]);
    } else {
      unknownOrders.push(order);
    }
  });
  const emptiedBaskets = findEmptiedBacksolveBaskets(
    selected.map(([, column]) => column),
    securities,
    alreadyDeletedIds
  );

  selected.forEach(([order, column]) => {
    const baskets = emptiedBaskets.get(column);
    if (baskets) {
      blocked.push({ order, baskets });
      return;
    }
    const name = nameOf(column, order);

    deletableOrders.push(order);
    names.push(name);

    if (column.has_multiple_investments && column.multiple_investments?.length) {
      pushImpact(`${name}: all tranches in Multiple Investments Dates.`);
    }
    const { fod_usage_details: fod, backsolve_usage_details: backsolve } = column;
    if (fod?.is_used_in_fod) {
      asList(fod.location).forEach(location => pushImpact(`${location} - its fund ownership rows will be deleted`));
    }
    if (backsolve?.is_used_in_backsolve) {
      asList(backsolve.location).forEach(location => pushImpact(`${location} - removed from its backsolve basket`));
    }
    if (fod?.is_used_in_fod || backsolve?.is_used_in_backsolve) {
      cascadeOrders.push(order);
      pushImpact('Cash flow ledger rows for these securities will also be deleted.');
    }
  });

  return { deletableOrders, cascadeOrders, blocked, names, messages, unknownOrders };
};

/** Dialog copy for the page-owned batch delete confirmation. */
export const presentCapTableBatchDelete = (plan: ColumnDeletePlan): ColumnDeletePresentation => {
  const reasons = describeEmptiedBaskets(plan.blocked.flatMap(blocked => blocked.baskets));
  if (plan.deletableOrders.length === 0) {
    return reasons.length ? { kind: 'blocked', reasons } : { kind: 'noop' };
  }
  const needsCascade = plan.cascadeOrders.length > 0;
  return {
    kind: 'confirm',
    title: needsCascade
      ? `Delete ${plan.names.join(', ')} everywhere?`
      : `Are you sure you want to permanently delete ${plan.names.join(', ')}?`,
    reasons,
    messages: plan.messages,
    confirmLabel: needsCascade ? 'Delete everywhere' : 'Delete',
  };
};
